import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '~/shadcn/components/ui/Button';
import { CheckCircle, Info } from 'lucide-react';
import { PrintPalletLabelAction } from '~/components/warehouse/components/locationInventoryV2Components/actions/printPalletLabelAction';
import { PrintCaseLabelAction } from '~/components/warehouse/components/locationInventoryV2Components/actions/printCaseLabelAction';

/**
 * Renders the success view (Step 4) for the pallet prep process, confirming completion and providing print options.
 * @returns {JSX.Element}
 */
function PrepStep4({ newPallets, newLotId }) {
    const [isPrintingPallet, setIsPrintingPallet] = useState(false);
    const [isPrintingRPCs, setIsPrintingRPCs] = useState(false);

    const palletRows = useMemo(() => {
        return (Array.isArray(newPallets) ? newPallets : [])
            .map((p) => ({
                palletId: p?.palletId,
                numRPCs: p?.numRPCs,
                looseUnits: p?.looseUnits,
            }))
            .filter((p) => p.palletId != null);
    }, [newPallets]);

    const palletCount = palletRows.length;

    const handlePrintPallet = () => {
        setIsPrintingPallet(true);
        setIsPrintingRPCs(false);
    };

    const handlePrintRPCs = () => {
        setIsPrintingRPCs(true);
        setIsPrintingPallet(false);
    };

    return (
        <div className="p-1 max-h-[65vh] overflow-y-auto space-y-4">
            <div className="flex items-center gap-4 px-4 py-3 bg-green-100/50 text-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                    <p className="font-semibold">Prep Process Complete</p>
                    <p className="text-sm">
                        The prep process has been successfully completed. The system has created{' '}
                        {palletCount === 1 ? 'a new pallet' : `${palletCount} new pallets`} for your prepped RPCs.
                    </p>
                </div>
            </div>

            <div className="p-4 border rounded-lg bg-blue-50/50">
                <div className="flex items-start gap-4">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                        <p className="font-semibold text-gray-800">System Information</p>
                        <p className="text-sm text-gray-600">
                            Prepped RPCs will be assigned to new system-created pallet(s). You can print labels for both
                            the pallet and individual RPCs.
                        </p>
                        {palletCount > 0 && (
                            <div className="mt-3 text-sm text-gray-700">
                                <p className="font-medium">Created pallet IDs</p>
                                <p className="text-xs text-gray-600 break-words">
                                    {palletRows
                                        .map(({ palletId, numRPCs, looseUnits }) => {
                                            const hasBreakdown = numRPCs != null || looseUnits != null;
                                            const breakdown = hasBreakdown
                                                ? ` (RPCs: ${numRPCs ?? 0}, loose units: ${looseUnits ?? 0})`
                                                : '';
                                            return `${palletId}${breakdown}`;
                                        })
                                        .join(', ')}
                                </p>
                            </div>
                        )}
                        <div className="mt-4 flex gap-2">
                            <Button variant="outline" onClick={handlePrintRPCs}>
                                Print RPC Labels
                            </Button>
                            <Button variant="outline" onClick={handlePrintPallet}>
                                Print Pallet Labels
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {(isPrintingPallet || isPrintingRPCs) && (
                <div className="gap-4 px-4 py-3 rounded-lg">
                    {isPrintingPallet && (
                        <>
                            <div>
                                <p className="font-semibold text-center">Print Pallet Labels</p>
                            </div>
                            {palletCount === 0 ? (
                                <p className="text-sm text-gray-600 text-center">
                                    No pallets were returned from the prep response.
                                </p>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        {palletRows.map(({ palletId, numRPCs, looseUnits }) => {
                                            const palletIdStr = String(palletId);
                                            const breakdown =
                                                numRPCs != null || looseUnits != null
                                                    ? `RPCs: ${numRPCs ?? 0}, loose units: ${looseUnits ?? 0}`
                                                    : 'No breakdown provided';

                                            return (
                                                <div
                                                    key={palletIdStr}
                                                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border rounded-md px-3 py-2 bg-white"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            Pallet {palletIdStr}
                                                        </p>
                                                        <p className="text-xs text-gray-600 truncate">{breakdown}</p>
                                                    </div>
                                                    <div className="md:shrink-0 md:self-end">
                                                        <PrintPalletLabelAction
                                                            palletId={palletIdStr}
                                                            onSuccess={() => setIsPrintingPallet(false)}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {isPrintingRPCs && (
                        <>
                            <div>
                                <p className="font-semibold text-center">Print RPC labels</p>
                            </div>
                            <PrintCaseLabelAction lotId={newLotId} onSuccess={() => setIsPrintingRPCs(false)} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default PrepStep4;
